// Libs
import { Options, Portal, Spinner, TextInput, View } from '@instructure/ui';
import { IconArrowOpenDownLine, IconArrowOpenUpLine } from '@instructure/ui-icons';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE, SELECTION_OPTION_ID } from '~/utils/constants/common';
// Components, Layouts, Pages
// Others
import { IBaseSelectOption } from '~/utils/interface/common';
// Styles, images, icons

export type IBaseSelectPayload = {
  page: number;
  searchKey?: string;
};

export type IBaseSelectResult = {
  optionsResp: IBaseSelectOption[];
  hasMoreResp: boolean;
};

type Props = {
  value?: string;
  label?: string;
  placeholder?: string;
  showDefaultOption?: boolean;
  disabled?: boolean;
  selectedLabel?: string;
  autoSelect?: boolean;
  callApiFn: (payload: IBaseSelectPayload) => Promise<IBaseSelectResult>;
  onChange?: (option?: IBaseSelectOption) => void;
  renderOptionSuffix?: (option: IBaseSelectOption, isHighlighted: boolean, isSelected: boolean) => React.ReactNode;
};

const VISIBLE_OPTIONS_COUNT = 8;
const OPTION_HEIGHT = 36;

const BaseDynamicSelect = (props: Props) => {
  //#region Destructuring Props
  const {
    value,
    label,
    placeholder,
    showDefaultOption = false,
    disabled = false,
    selectedLabel,
    autoSelect: autoSelectFirst = false,
    onChange,
    renderOptionSuffix,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Declare State
  const [inputValue, setInputValue] = useState<string>();
  const [isShowingOptions, setIsShowingOptions] = useState<boolean>(false);
  const [highlightedOptionId, setHighlightedOptionId] = useState<string | undefined>();
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>();
  const selectedOptionIdRef = useRef<string | undefined>();
  const [inputContainerEl, setInputContainerEl] = useState<HTMLSpanElement | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Dynamic mode state
  const [dynamicOptions, setDynamicOptions] = useState<IBaseSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const outerRef = useRef<HTMLSpanElement | null>(null);
  // Refs to avoid stale closures in async fetchData
  const callApiFnRef = useRef(props.callApiFn);
  const autoSelectFirstRef = useRef(autoSelectFirst);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const pageRef = useRef<number>(DEFAULT_PAGE);
  const hasMoreRef = useRef<boolean>(true);
  const isLoadingRef = useRef<boolean>(false);
  //#endregion Declare State

  const setSelectedOptionIdSync = (id: string | undefined) => {
    selectedOptionIdRef.current = id;
    setSelectedOptionId(id);
  };

  const options = dynamicOptions;

  //#region Implement Hook
  useEffect(() => {
    callApiFnRef.current = props.callApiFn;
    autoSelectFirstRef.current = autoSelectFirst;
    valueRef.current = value;
    onChangeRef.current = onChange;
  }, [props.callApiFn, autoSelectFirst, value, onChange]);

  useEffect(() => {
    const option = options.find((option) => option.value === value);
    if (option) {
      setInputValue(option.label);
      setSelectedOptionIdSync(option.value);
      return;
    }
    if (value && selectedLabel) {
      setInputValue(selectedLabel);
      setSelectedOptionIdSync(value);
      return;
    }
    setInputValue(undefined);
    setSelectedOptionIdSync(undefined);
  }, [options, value, selectedLabel]);

  const updateDropdownPosition = useCallback(() => {
    if (outerRef.current) {
      const rect = outerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, []);

  useEffect(() => {
    handleFetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (isShowingOptions) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isShowingOptions, updateDropdownPosition]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleFetchData = useCallback(async (isFirstLoad: boolean = false) => {
    if (!callApiFnRef.current) return;
    if (isLoadingRef.current) return;
    if (!isFirstLoad && !hasMoreRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const currentPage = isFirstLoad ? DEFAULT_PAGE : pageRef.current;
      const resp = await callApiFnRef.current({ page: currentPage });
      const { optionsResp, hasMoreResp } = resp;

      setDynamicOptions((prev) => (isFirstLoad ? optionsResp : [...prev, ...optionsResp]));
      hasMoreRef.current = hasMoreResp;
      pageRef.current = currentPage + 1;

      if (isFirstLoad && autoSelectFirstRef.current && optionsResp.length > 0 && !valueRef.current) {
        const first = optionsResp[0];
        setSelectedOptionIdSync(first.value);
        setInputValue(first.label);
        onChangeRef.current?.(first);
      }
    } catch (_) {
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  const handleShowOptions = () => {
    if (disabled) return;
    updateDropdownPosition();
    setIsShowingOptions(true);
  };

  const handleHideOptions = () => {
    setIsShowingOptions(false);
    setHighlightedOptionId(undefined);
    const currentSelectedId = selectedOptionIdRef.current;
    const option = options.find((o) => o.value === currentSelectedId);
    setInputValue(
      currentSelectedId ? (option?.label ?? (value && selectedLabel ? selectedLabel : undefined)) : undefined
    );
  };

  const handleSelectOption = (option: IBaseSelectOption) => {
    setSelectedOptionIdSync(option.value);
    setInputValue(option.label);
    setIsShowingOptions(false);
    setHighlightedOptionId(undefined);
    onChange?.(option);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      handleFetchData(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const allOptions = showDefaultOption
      ? [{ value: SELECTION_OPTION_ID, label: t('common_base_select_label') }, ...options]
      : options;
    const currentIndex = allOptions.findIndex((o) => o.value === highlightedOptionId);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isShowingOptions) {
        handleShowOptions();
        return;
      }
      const nextIndex = currentIndex < allOptions.length - 1 ? currentIndex + 1 : 0;
      setHighlightedOptionId(allOptions[nextIndex]?.value);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isShowingOptions) return;
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : allOptions.length - 1;
      setHighlightedOptionId(allOptions[prevIndex]?.value);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isShowingOptions) {
        handleShowOptions();
        return;
      }
      if (highlightedOptionId) {
        const option = allOptions.find((o) => o.value === highlightedOptionId);
        if (option && option.value !== SELECTION_OPTION_ID) {
          handleSelectOption(option);
        } else if (option?.value === SELECTION_OPTION_ID) {
          setSelectedOptionIdSync(undefined);
          setInputValue(undefined);
          setIsShowingOptions(false);
          onChange?.(undefined);
        }
      }
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      handleHideOptions();
    }
  };
  //#endregion Handle Function

  return (
    <span ref={outerRef} style={{ display: 'block', position: 'relative' }} onKeyDown={handleKeyDown}>
      <TextInput
        renderLabel={label}
        value={inputValue ?? placeholder ?? t('common_base_select_label')}
        interaction={disabled ? 'disabled' : 'readonly'}
        isRequired={false}
        renderAfterInput={
          <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
            {isShowingOptions ? <IconArrowOpenUpLine inline={false} /> : <IconArrowOpenDownLine inline={false} />}
          </span>
        }
        inputContainerRef={(el) => {
          setInputContainerEl(el);
        }}
        onClick={isShowingOptions ? handleHideOptions : handleShowOptions}
        onBlur={() => {
          setTimeout(() => {
            if (!inputContainerEl?.contains(document.activeElement)) {
              handleHideOptions();
            }
          }, 150);
        }}
        onChange={() => {}}
      />
      <Portal open={isShowingOptions && !!inputContainerEl}>
        <div style={dropdownStyle}>
          <View as='div' background='primary' shadow='resting'>
            <div
              style={{
                overflowY: 'auto',
                maxHeight: OPTION_HEIGHT * VISIBLE_OPTIONS_COUNT,
              }}
              onScroll={handleScroll}
            >
              <Options as='ul'>
                {showDefaultOption && (
                  <Options.Item
                    as='li'
                    variant={
                      highlightedOptionId === SELECTION_OPTION_ID
                        ? 'highlighted'
                        : selectedOptionId === undefined
                          ? 'selected'
                          : undefined
                    }
                    onMouseEnter={() => setHighlightedOptionId(SELECTION_OPTION_ID)}
                    onMouseLeave={() => setHighlightedOptionId(undefined)}
                    onClick={() => {
                      setSelectedOptionIdSync(undefined);
                      setInputValue(undefined);
                      setIsShowingOptions(false);
                      onChange?.(undefined);
                    }}
                  >
                    {t('common_base_select_label')}
                  </Options.Item>
                )}
                {options.map((option) => (
                  <Options.Item
                    as='li'
                    key={option.value}
                    variant={
                      option.value === highlightedOptionId
                        ? 'highlighted'
                        : option.value === selectedOptionId
                          ? 'selected'
                          : undefined
                    }
                    renderBeforeLabel={option.renderBeforeLabel}
                    onMouseEnter={() => setHighlightedOptionId(option.value)}
                    onMouseLeave={() => setHighlightedOptionId(undefined)}
                    onClick={() => handleSelectOption(option)}
                  >
                    {renderOptionSuffix ? (
                      <span
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <span>{option.label}</span>
                        <span data-option-suffix='true'>
                          {renderOptionSuffix(
                            option,
                            option.value === highlightedOptionId,
                            option.value === selectedOptionId
                          )}
                        </span>
                      </span>
                    ) : (
                      option.label
                    )}
                  </Options.Item>
                ))}
                {!isLoading && options.length === 0 && !showDefaultOption && (
                  <Options.Item as='li' variant={undefined}>
                    <span style={{ display: 'flex', justifyContent: 'center', padding: '4px 0', color: '#6B7280' }}>
                      {t('common_no_data_available')}
                    </span>
                  </Options.Item>
                )}
                {isLoading && (
                  <Options.Item as='li' variant={undefined}>
                    <span style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
                      <Spinner size='x-small' renderTitle={t('common_base_loading_label')} />
                    </span>
                  </Options.Item>
                )}
              </Options>
            </div>
          </View>
        </div>
      </Portal>
    </span>
  );
};

export default BaseDynamicSelect;
